import {
  FrontendContextV1,
  type CentraProductPage,
} from "@made-people/nestjs-bff-service";
import { ProductTemplate } from "@made-people/blueprint-product";
import { ProductProvider } from "@made-people/grease-product";
import { BreadCrumbsProvider } from "@made-people/grease-bread-crumbs";
import { WashingInstructionsBlock } from "../components/features/washing-instructions-block";
import { SizeGuideTrigger } from "../components/features/size-guide";
import { PDPTrustLogos } from "../components/features/pdp-trust-logos";
import { frontendProductToGreaseRelatedProducts } from "../services/bff/mappers/frontend-product-to-grease-related-products";
import { frontendProductToGreaseProduct } from "../services/bff/mappers/frontend-product-to-grease-product";
import { useGeneralSettings } from "../hooks/use-general-settings";
import { StoryblokComponent } from "@made-people/grease-storyblok";
import { SbBlokData } from "@storyblok/js";
import { ProductReviewsSneakpeek } from "../components/features/trustpilot/components/product-reviews-sneakpeek";
import { type MmLoaderCustomExtraData } from "../utils/loader-custom-extra-data";
import { useTechnicalFeatures } from "../hooks/use-technical-features-settings";
import { ComfortAccordion } from "../components/features/comfort-accordion/comfort-accordion";
import { CustomisationDrawerBox } from "../components/comfort-suite/customisation/customisation-drawer-box";
import { FeatureToggles } from "../components/comfort-suite/feature-toggle/feature-toggles";
import { StylePicker } from "../components/comfort-suite/style-picker/style-picker";
import { useTrustpilotPdpData } from "../services/trustpilot/use-trustpilot-pdp-data";
import { TrustpilotReviews } from "../components/features/trustpilot/components/trustpilot-reviews";

/**
 * A Centra base PDP page.
 */

export const CentraProductPageRoute = (props: {
  page: CentraProductPage;
  frontendContext: FrontendContextV1;
  customExtraData?: MmLoaderCustomExtraData;
}) => {
  const { productData, breadCrumbs } = props.page;
  const generalSettings = useGeneralSettings();
  const { trustpilotProductReviews, trustpilotProductSku } =
    useTrustpilotPdpData();

  const technicalFeatures = useTechnicalFeatures();

  const filteredTechnicalFeatures = technicalFeatures.filter(
    ({ heading: technicalFeatureHeading }) =>
      productData?.technical_features?.includes(technicalFeatureHeading),
  );

  const belowTheFoldComponents = [
    ...(generalSettings?.belowTheFoldComponents || []),
  ]?.map((component: SbBlokData, index: number) => (
    <StoryblokComponent key={index} blok={component} />
  ));

  const product = frontendProductToGreaseProduct(
    productData,
    props.frontendContext,
  );

  const relatedProducts = frontendProductToGreaseRelatedProducts(
    productData,
    props.frontendContext,
  );

  return (
    <ProductProvider
      product={product}
      relatedProducts={relatedProducts}
      context={{ type: "pdp" }}
    >
      <BreadCrumbsProvider breadCrumbs={breadCrumbs}>
        <ProductTemplate
          template="vienna"
          beforeSiblingsSlot={
            <>
              <StylePicker />
              <FeatureToggles />
            </>
          }
          afterSiblingsSlot={
            <>
              <CustomisationDrawerBox />
            </>
          }
          siblingsPickerSettings={{
            template: "color-swatch",
            colorSwatchAttributeKey: "product_color_image_url",
          }}
          reviewStarsComponent={
            <ProductReviewsSneakpeek reviewsData={trustpilotProductReviews} />
          }
          sizeGuideComponentSlot={<SizeGuideTrigger />}
          afterAddToCartSlot={<PDPTrustLogos />}
          afterAttributesSlot={
            <ComfortAccordion items={filteredTechnicalFeatures ?? []} />
          }
          detailsComponentSlot={<WashingInstructionsBlock />}
          belowTheFoldComponents={
            <div className="col-span-6 col-start-1">
              {belowTheFoldComponents}
            </div>
          }
        />
      </BreadCrumbsProvider>
      <TrustpilotReviews
        type="product"
        productSku={trustpilotProductSku}
        reviewsData={trustpilotProductReviews}
      />
    </ProductProvider>
  );
};
